import S from 'common/service/sanctuary';
import { compose, without, prop } from 'ramda';
import React from 'react';
import { connect } from 'react-redux';
import { func } from 'prop-types';
import { defineMessages, injectIntl, intlShape, FormattedMessage } from 'react-intl';
import { CollectionWrapper } from 'common/mdc/wrappers';
import Button from 'common/mdc/button';
import { StepperContent } from 'common/mdc/stepper';
import { SwitchField } from 'common/mdc/form';
import { ToolbarSection } from 'common/mdc/toolbar';
import { Pagination } from 'common/mdc/table';
import withMultiSelection from 'common/mdc/table/multiSelection';
import Form, { Error, DisplayField } from "common/mdc/form/index";
import TextField from "common/mdc/textfield/index";
import RadioField from 'common/mdc/form/radio';
import { CheckboxField } from 'common/mdc/form';
import "../steps/createNewJob.scss";
import { validateJobName, validateCallbackUrl } from "../validator";
import { actions } from "@sma/store/job/jobs";

export const STEP_ID = Symbol('refine-selection');

const messages = defineMessages({
  title: {
    id: 'jobCreate.title.step1',
    defaultMessage: 'Job Details',
  },
  continue: {
    id: 'jobCreate.actions.continue',
    defaultMessage: 'Continue',
  },
});

export const title = <FormattedMessage {...messages.title} />;

class Step1 extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      jobName: "",
      jobType: null,
      isCallBackEnabled: false,
      jobMeterCallback: false,
      jobCallback: false,
      jobCallbackUrl: "",
      uiError: null,
      isContinueDisabled: true
    };

    this.proceed = this.proceed.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.setChecked = this.setChecked.bind(this);
    this.checkForDuplicate = this.checkForDuplicate.bind(this);
    this.checkToEnableContinue = this.checkToEnableContinue.bind(this);

  }

  componentDidMount() {
    const { jobName, jobType, isCallBackEnabled, jobMeterCallback, jobCallback, jobCallbackUrl } = this.props.newJobEntries || {};
    this.setState({
      jobName, jobType, isCallBackEnabled, jobMeterCallback, jobCallback, jobCallbackUrl, isContinueDisabled: !jobName.length > 0
    });
  }

  proceed() {
    const { jobName, jobType, isCallBackEnabled, jobMeterCallback, jobCallback, jobCallbackUrl } = this.state;
    const jobDetails = { jobName, jobType, isCallBackEnabled, jobMeterCallback, jobCallback, jobCallbackUrl };
    const {  duplicateJobCheck, stepper, saveStep1Details } = this.props;
    const { jobName: prevJobName, } = this.props.newJobEntries || {};

    if(prevJobName !== undefined && prevJobName.length > 0){
      saveStep1Details(jobDetails);
      return stepper.resolve({});
    }

    if (jobName.length >= 5 && jobName.length <= 200) {
      duplicateJobCheck(jobName, jobDetails, stepper, saveStep1Details);
    }
  }

  checkToEnableContinue() {
    const { jobName, jobType, isCallBackEnabled, jobMeterCallback, jobCallback, jobCallbackUrl } = this.state;
    if (jobName.length >= 5 && jobName.length <= 200 && jobType !== null) {
      if (isCallBackEnabled === true) {
        if ((jobMeterCallback === true || jobCallback === true) && jobCallbackUrl.length > 7 && jobCallbackUrl.length <= 2048) {
          this.setState({ isContinueDisabled: false });
          return;
        }
        else {
          this.setState({ isContinueDisabled: true });
          return;
        }
      }
      this.setState({ isContinueDisabled: false });
      return;
    }
  }

  handleChange(inputFor, text) {
    const { isJobDuplicate, duplicateJobCheck, resetDuplicateJobCheck } = this.props;
    const { jobName: prevJobName, } = this.props.newJobEntries || {};
    if (inputFor === "jobName") {
      if (isJobDuplicate === true || (prevJobName !== undefined && prevJobName.length > 0)) {
        resetDuplicateJobCheck();
      }
    }
    this.setState({ [inputFor]: text }, () => {
      const { jobName, jobType, isCallBackEnabled, jobMeterCallback, jobCallback, jobCallbackUrl } = this.state;
      const isInValidJobName = validateJobName(jobName);
      if (isInValidJobName !== undefined) {
        this.setState({
          uiError: isInValidJobName,
          isContinueDisabled: true
        });
        return;
      }

      if (isCallBackEnabled === true && jobCallbackUrl.length > 0) {
        const isValidCallbackUrl = validateCallbackUrl(jobCallbackUrl);
        if (!isValidCallbackUrl) {
          this.setState({
            uiError: "Enter a valid URL.",
            isContinueDisabled: true
          });
          return;
        }
      }

      this.setState({
        uiError: null,
      });

      if (jobName.length < 5) {
        this.setState({
          uiError: null,
          isContinueDisabled: true
        }, () => {
          resetDuplicateJobCheck();
        });
        return;
      }
      this.checkToEnableContinue();
    });
  }

  checkForDuplicate(jobName) {
    const { duplicateJobCheck } = this.props;
    if (jobName.length >= 5 && jobName.length <= 200) {
      duplicateJobCheck(jobName);
      this.checkToEnableContinue();
    }
  }

  setChecked(checked) {
    this.setState({ jobType: checked }, () => {
      this.checkToEnableContinue();
    })
  }

  render() {
    const { stepper, cancelAction, isJobDuplicate, duplicateError, } = this.props;
    const { jobName, jobType, isCallBackEnabled, jobCallbackUrl, jobMeterCallback, jobCallback, uiError, isContinueDisabled } = this.state;

    return (
      <StepperContent
        loading={false}
        onSubmit={this.proceed}
        title={title}
        actions={[
          cancelAction,
          <Button raised type="submit" disabled={isContinueDisabled || isJobDuplicate}>
            <FormattedMessage {...messages.continue} />
          </Button>,
        ]}
        className="job-details-form-container"
      >
        <Form className={"create-job-form"}>
          {uiError && <Error><span title={uiError}>{uiError}</span></Error>}
          {isJobDuplicate && <Error><span title={duplicateError}>{duplicateError}</span></Error>}
          <div className='job-name-input-container'>
            <DisplayField label={"Job Name"} isRequired>
              <TextField
                type="text"
                placeholder="Enter job name (5-200 characters, A-Z a-z 0-9 _ . @ #)"
                value={jobName}
                onChange={(e) => this.handleChange("jobName", e.target.value)}
                fullWidth
              />
            </DisplayField>
          </div>
          <div className='job-type-input-container'>
            <DisplayField label={"Job Type"} isRequired>
              <RadioField
                id={"job-type-meter-configuration"}
                checked={jobType === "MeterConf"}
                onChange={() => this.setChecked("MeterConf")}
                label={"Meter Configuration"}
              />
              <RadioField
                id={"job-type-meter-firmware-upgrade"}
                checked={jobType === "MeterFirmwareUpgrade"}
                onChange={() => this.setChecked("MeterFirmwareUpgrade")}
                label={"Meter Firmware Upgrade"}
                disabled
              />
              <RadioField
                id={"job-type-meter-action"}
                checked={jobType === "MeterAction"}
                onChange={() => this.setChecked("MeterAction")}
                label={"Meter Action"}
                disabled
              />
            </DisplayField>

          </div>
          <div className='job-callback-input-container'>
            <CheckboxField
              label={'Enable Callback'}
              className={"enable-callback-option"}
              id={`enable-callback-for-job`}
              checked={isCallBackEnabled}
              onChange={() => this.handleChange("isCallBackEnabled", !isCallBackEnabled)}
            />
            {isCallBackEnabled ?
              <div className='callback-on-container'>
                <DisplayField label={"Callback On"} isRequired>
                  <div className='callback-select-container'>
                    <CheckboxField
                      label={'JOB_METER_CALLBACK'}
                      id={`enable-job-meter-callback`}
                      checked={jobMeterCallback}
                      onChange={() => this.handleChange("jobMeterCallback", !jobMeterCallback)}
                    />
                    <CheckboxField
                      label={'JOB_CALLBACK'}
                      id={`enable-job-call-back`}
                      checked={jobCallback}
                      onChange={() => this.handleChange("jobCallback", !jobCallback)}
                    />
                  </div>
                </DisplayField>
                <DisplayField label={"Callback URL"} isRequired className={"callback-url-field"}>
                  <TextField
                    type="text"
                    placeholder="https://domain.com"
                    onChange={(e) => this.handleChange("jobCallbackUrl", e.target.value)}
                    fullWidth
                    value={jobCallbackUrl}
                  />
                </DisplayField>
              </div>
              : null
            }
          </div>
        </Form>

      </StepperContent>
    );
  }
}

Step1.defaultProps = {
  isJobDuplicate: false,
};

export default compose(
  connect(
    state => ({
      duplicateError: state.job.jobs.createJob.error,
      isJobDuplicate: state.job.jobs.createJob.isJobDuplicate,
      newJobEntries: state.job.jobs.createJob
    }),
    {
      duplicateJobCheck: actions.duplicateJobCheck,
      resetDuplicateJobCheck: actions.resetDuplicateJobCheck,
      saveStep1Details: actions.saveStep1Details,
    },
  ),
  injectIntl,
)(Step1);
